<template>
  <div class="skribble-poa-proof card pr-4 pl-4 pt-4 pb-1">
    <div v-if="initializing" class="text-center py-5">
      <b-spinner variant="primary" label="loading" />
    </div>

    <div v-else>
      <div v-if="documentSigned" class="text-center text-success font-medium-5 mb-2">
        {{ $t('skr_document_signed') }}
      </div>

      <div v-else>
        <div class="mb-4">
          <h3 class="h5 mb-3 text-primary"  style="font-weight: 700;font-size: 20px;margin-top: 0;--text-opacity: 1;">
            {{ $t('identification_title') }}
          </h3>
        </div>

        <div v-if="warningPhone" class="alert alert-warning">
          {{ warningPhone }}
        </div>

        <div v-if="pdfUrl && !showSuccess" class="pdf-preview-wrapper mb-3">
          <iframe
              class="pdf-preview"
              :src="pdfUrl"
              title="Power of Attorney Preview"
          />
        </div>

        <div v-else-if="!showSuccess" class="alert alert-secondary">
          {{ $t('preview_unavailable') }}
        </div>

        <div v-if="!showSuccess" class="d-flex flex-wrap align-items-center justify-content-end mt-3">
          <b-button
              type="button"
              variant="primary"
              class="mr-2 mb-2"
              :disabled="sending || !canSend"
              @click="sendForSigning"
          >
            <span v-if="sending">
              <b-spinner small type="grow" class="mr-1" />
              {{ $t('sending') }}
            </span>
            <span v-else>{{ $t('skr_btn') }}</span>
          </b-button>
        </div>

        <SkribblePopup
            :show="showPopup"
            @cancel="showPopup = false"
            @continue="handlePopupConfirm"
        />

        <div
            v-if="showSuccess"
            class="text-primary mt-3 font-medium-4 fw-semibold"
        >
          {{ $t('document_sent_for_signing') }}
          <a
              v-if="signingUrl"
              :href="signingUrl"
              target="_blank"
              class="text-info fw-bold fs-5 text-decoration-underline"
          >
            {{ $t('continue_signing_here') }}
          </a>
        </div>

        <div v-if="error" class="alert alert-danger mt-3">
          {{ error }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BButton,
  BFormGroup,
  BFormInput,
  BSpinner,
} from "bootstrap-vue";
import moment from "moment";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  name: "PowerOfAttorneyAndProofOfInterest",
  components: {
    BButton,
    BFormGroup,
    BFormInput,
    BSpinner,
  },
  props: {
  },
  data() {
    return {
      formType: "power_of_attorney_and_proof_of_interest",
      form: null,
      pdfUrl: null,
      signingUrl: null,
      showSuccess: false,
      showPopup: false,
      documentSigned: false,
      initializing: true,
      previewLoading: false,
      pendingPreview: false,
      checkingStatus: false,
      activating: false,
      sending: false,
      error: null,
      warningPhone: null,
      pollInterval: null,
      currentUserId: null,
      company: null,
    };
  },
  async created() {
    try {
      await this.initialize();
    } catch (err) {
      this.error = this.extractError(err);
    } finally {
      this.initializing = false;
    }
  },
  beforeDestroy() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }
  },
  computed: {
    canSend() {
      if (!this.form) return false;
      return Boolean(
          this.form.sign_email &&
          this.form.sign_mobile &&
          this.form.sign_phone_code &&
          !this.previewLoading &&
          !this.pendingPreview
      );
    },
  },
  methods: {
    async initialize() {
      const user = await this.refreshUser();
      if (!user) {
        throw new Error("Unable to load user data.");
      }

      this.currentUserId = user.id;
      const companyId = user.company_id || user?.company?.id;
      if (companyId) {
        await this.fetchCompany(companyId);
      }

      this.prepareForm(user);

      await this.checkSigningStatus();

      if (!this.form.sign_mobile) {
        this.warningPhone = this.$t("add_your_phone_number");
      }
    },

    async refreshUser() {
      try {
        let user = this.$store.state.auth.user;
        if (user) {
          const { data } = await this.$http.post(`/getUserDataWithId/${user.id}`);
          if (data && data.value) {
            this.$store.commit("auth/setUser", data.value);
            return data.value;
          }
          return user;
        }
        return null;
      } catch (err) {
        console.error("Failed to refresh user", err);
        return this.$store.state.auth.user;
      }
    },

    async fetchCompany(companyId) {
      try {
        const response = await this.$http.post(`/company/show/${companyId}`);
        this.company = response.data?.value ?? null;
      } catch (err) {
        console.error("Failed to fetch company", err);
        this.company = null;
      }
    },

    prepareForm(user) {
      const company = this.company || user?.company || {};
      const companyAddressParts = [company.address, company.address_nr].filter(Boolean);

      this.form = {
        admin_first: user?.first_name || "",
        admin_last: user?.last_name || "",
        admin_email: user?.email || "",

        sign_first: user?.first_name || "",
        sign_last: user?.last_name || "",
        sign_email: user?.email || "",
        sign_mobile:  user?.phone || "",
        sign_phone_code: user?.country?.iso_code || "CH",
        sign_function: user?.position || "",

        signature_place: company?.city || "Zug",
        signature_date: moment().format("DD.MM.YYYY"),

        company_name: company?.name || "",
        company_address: companyAddressParts.join(" "),
        company_zip: company?.zip || "",
        company_city: company?.city || "",
        company_country: company?.country|| "",
      };
    },

    async generatePreview() {
      if (!this.form) return;
      this.previewLoading = true;
      this.error = null;
      try {
        const payload = {
          form: this.form,
          form_type: this.formType,
          language: this.$i18n?.locale || "de",
        };
        const res = await this.$http.post("/skribble/power-of-attorney/generate", payload);
        this.pdfUrl = res?.data?.value?.pdf_url || null;
        this.signingUrl = res?.data?.value?.signing_url || null;
        this.pendingPreview = false;
      } catch (err) {
        console.error("Failed to generate preview", err);
        this.error = this.extractError(err);
      } finally {
        this.previewLoading = false;
      }
    },

    async sendForSigning() {
      if (!this.canSend) return;
      this.sending = true;
      this.error = null;
      try {
        if (this.signingUrl) {
          this.showSuccess = true;
          this.handleButtonClick();
        }

      } catch (err) {
        console.error("Failed to send document for signing", err);
        this.error = this.extractError(err);
      } finally {
        this.sending = false;
      }
    },

    openSigningUrl() {
      if (this.signingUrl) {
        window.open(this.signingUrl, "_blank");
        this.startPolling();
      }
    },

    async checkSigningStatus() {
      if (!this.currentUserId) return;

      if (this.checkingStatus) return;
      this.checkingStatus = true;

      try {
        const res = await this.$http.post(`/skribble/check-status/${this.currentUserId}/${this.formType}`);
        const status = res?.data?.value;
        this.signingUrl = res?.data?.signing_url;

        if (status === "SIGNED") {
          if (this.pollInterval) {
            clearInterval(this.pollInterval);
            this.pollInterval = null;
          }

          this.documentSigned = true;
          this.showSuccess = false;

          if (this.activating) return;
          this.activating = true;

          try {
            const activateRes = await this.$http.post('/company/activate');
            if (activateRes.data.status === 200) {
              this.$emit("save-requested");
            } else {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: this.$t(activateRes.data.message || 'Activation Failed'),
                  icon: 'AlertTriangleIcon',
                  variant: 'danger',
                },
              });
            }
          } catch (err) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: this.$t('activation_failed'),
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            });
          } finally {
            this.activating = false;
          }

        } else if (status === "PENDING" || status === "UPDATED") {
          this.showSuccess = true;
          if (!this.pollInterval) {
            this.startPolling();
          }
        } else {
          if (!this.signingUrl || (!this.pdfUrl && !this.previewLoading)) {
            await this.generatePreview();
          }
        }
      } catch (err) {
        console.error("Failed to check signing status", err);
      } finally {
        this.checkingStatus = false;
      }
    },

    startPolling() {
      if (this.pollInterval) {
        return;
      }

      this.pollInterval = setInterval(() => {
        this.checkSigningStatus();
      }, 5000);
    },
    handlePopupConfirm() {
      this.showPopup = false;
      this.openSigningUrl();
    },

    handleButtonClick() {
      if (localStorage.getItem("hideSkribblePopup") === "true") {
        this.openSigningUrl();
      } else {
        this.showPopup = true;
      }
    },

    extractError(err) {
      if (err?.response?.data?.message) return err.response.data.message;
      if (err?.message) return err.message;
      return this.translate("something_went_wrong", "Something went wrong. Please try again.");
    },
  },
};
</script>

<style scoped>
.skribble-poa-proof {
  max-width: 1500px;
  margin: 0 auto;
  background: #f4f5f7;
  border: 0;
}

.pdf-preview-wrapper {
  position: relative;
  padding-top: 65%;
  width: 100%;
}

.pdf-preview {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
  background: #ffffff;
}
</style>

